import {ZoneContextManager} from '@opentelemetry/context-zone';
import {ZipkinExporter} from '@opentelemetry/exporter-zipkin';
import {B3InjectEncoding, B3Propagator} from '@opentelemetry/propagator-b3';
import {registerInstrumentations} from '@opentelemetry/instrumentation';
import {DocumentLoadInstrumentation} from '@opentelemetry/instrumentation-document-load';
import {FetchInstrumentation} from '@opentelemetry/instrumentation-fetch';
import {XMLHttpRequestInstrumentation} from '@opentelemetry/instrumentation-xml-http-request';
import {resourceFromAttributes} from '@opentelemetry/resources';
import {
    BatchSpanProcessor,
    type ReadableSpan,
    type Span,
    type SpanProcessor,
} from '@opentelemetry/sdk-trace-base';
import {WebTracerProvider} from '@opentelemetry/sdk-trace-web';
import {ATTR_SERVICE_NAME} from '@opentelemetry/semantic-conventions';

// `deployment.environment.name` is an incubating semantic convention that lives
// behind the `/incubating` package export. Our tsconfig uses moduleResolution=node
// which doesn't honor exports map, so we use the literal string instead of
// importing `ATTR_DEPLOYMENT_ENVIRONMENT_NAME`. Spec:
// https://opentelemetry.io/docs/specs/semconv/resource/deployment-environment/
const ATTR_DEPLOYMENT_ENVIRONMENT_NAME = 'deployment.environment.name';

export interface TracingConfig {
    /** `service.name` resource attribute (e.g. `ngwebbill`). */
    serviceName: string;
    /** `deployment.environment.name` resource attribute (e.g. `dev`). */
    environment: string;
    /** Zipkin v2 endpoint, e.g. `https://webbill-dev.brunata.dk/otel/api/v2/spans`.
     *  Empty disables tracing (typical for local dev). */
    endpoint: string;
}

let currentUsername = '';

/**
 * Stamps every span on start with `UserName=<currentUsername>` — matches the
 * attribute the Webservices backend already sets, so the same Grafana dashboard
 * columns light up for both browser and backend spans.
 */
class UserAttributeSpanProcessor implements SpanProcessor {
    onStart(span: Span): void {
        if (currentUsername) {
            span.setAttribute('UserName', currentUsername);
        }
    }

    onEnd(_: ReadableSpan): void {
        // no-op
    }

    forceFlush(): Promise<void> {
        return Promise.resolve();
    }

    shutdown(): Promise<void> {
        return Promise.resolve();
    }
}

/**
 * Bootstraps OpenTelemetry browser tracing. Call ONCE from `main.ts` BEFORE
 * `bootstrapModule` so the document-load span captures the initial page load.
 *
 * Trace context is propagated using B3 multi-header (X-B3-TraceId / SpanId /
 * Sampled), which the backend Brave instrumentation reads by default. For
 * same-origin requests — the only kind these apps actually make to their own
 * webservice — browser spans and backend spans share one TraceID and stitch
 * into a single end-to-end chain in Tempo. Cross-origin propagation is
 * deliberately disabled (see comment on the registerInstrumentations call
 * below) so third-party endpoints don't trip CORS preflight rejections.
 */
export function initTracing(config: TracingConfig): void {
    if (!config.endpoint) {
        // Silent no-op when no endpoint configured (typical for local dev).
        return;
    }

    const exporter = new ZipkinExporter({
        url: config.endpoint,
        serviceName: config.serviceName,
    });

    const provider = new WebTracerProvider({
        resource: resourceFromAttributes({
            [ATTR_SERVICE_NAME]: config.serviceName,
            [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: config.environment,
        }),
        spanProcessors: [new UserAttributeSpanProcessor(), new BatchSpanProcessor(exporter)],
    });

    // Use B3 multi-header propagation (X-B3-TraceId, X-B3-SpanId, X-B3-Sampled)
    // so the backend Brave instrumentation reads the trace context and continues
    // the trace started in the browser. The backend default is B3; this aligns
    // frontend with backend so browser→backend traces are stitched into one chain.
    provider.register({
        contextManager: new ZoneContextManager(),
        propagator: new B3Propagator({
            injectEncoding: B3InjectEncoding.MULTI_HEADER,
        }),
    });

    // Same-origin requests always get trace headers (default OTel behavior).
    // We deliberately do NOT propagate to ANY cross-origin URL — third parties
    // (Keycloak, Microsoft SSO, sharepoint, etc.) don't allow X-B3-* headers in
    // their CORS allow-headers, so propagating would trigger preflight failures
    // (HTTP 0 / Unknown Error) and break the page. Cross-origin tracing can be
    // added later per allow-listed URL once those backends opt-in via CORS.
    //
    // ignoreUrls excludes the exporter endpoint itself: ZipkinExporter posts
    // spans via XHR in the browser (sendBeacon is only used on page unload),
    // so without this each export call would be instrumented as a new span,
    // producing a self-feeding export loop.
    registerInstrumentations({
        instrumentations: [
            new DocumentLoadInstrumentation(),
            new XMLHttpRequestInstrumentation({
                propagateTraceHeaderCorsUrls: [],
                ignoreUrls: [config.endpoint],
            }),
            new FetchInstrumentation({
                propagateTraceHeaderCorsUrls: [],
                ignoreUrls: [config.endpoint],
            }),
        ],
    });
}

/**
 * Sets the authenticated username on subsequent spans (and any spans starting
 * after this call). Call after the current user is known.
 */
export function setTracedUser(username: string): void {
    currentUsername = username;
}
